// Validation Types and Schemas for Invoice System
// This file contains validation schemas and utility types for form validation

import { z } from "zod";

// =====================================================
// COMMON VALIDATION SCHEMAS
// =====================================================

// Base schemas for reuse
export const uuidSchema = z.string().uuid("Invalid UUID format");
export const emailSchema = z.string().email("Invalid email format");
export const phoneSchema = z
  .string()
  .min(1, "Phone number is required")
  .optional();
export const positiveNumberSchema = z
  .number()
  .positive("Must be a positive number");
export const nonNegativeNumberSchema = z
  .number()
  .min(0, "Must be zero or positive");
export const percentageSchema = z
  .number()
  .min(0)
  .max(100, "Must be between 0 and 100");
export const hexColorSchema = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/, "Must be a valid hex color");

// =====================================================
// PAYMENT RECORD VALIDATION
// =====================================================

export const paymentMethodSchema = z.enum([
  "cash",
  "card",
  "mobile",
  "bank_transfer",
  "check",
  "other",
]);

export const paymentRecordSchema = z.object({
  invoiceId: uuidSchema,
  paymentDate: z.date(),
  amount: positiveNumberSchema,
  paymentMethod: paymentMethodSchema,
  referenceNumber: z.string().optional(),
  notes: z
    .string()
    .max(1000, "Notes must be less than 1000 characters")
    .optional(),
});

export const paymentRecordFormSchema = z.object({
  invoiceId: z.string().min(1, "Invoice is required"),
  paymentDate: z.string().min(1, "Payment date is required"),
  amount: z
    .string()
    .min(1, "Amount is required")
    .refine(
      (val) => !isNaN(Number(val)) && Number(val) > 0,
      "Amount must be a positive number"
    ),
  paymentMethod: paymentMethodSchema,
  referenceNumber: z.string().optional(),
  notes: z
    .string()
    .max(1000, "Notes must be less than 1000 characters")
    .optional(),
});

// =====================================================
// INVOICE TEMPLATE VALIDATION
// =====================================================

export const customFieldTypeSchema = z.enum([
  "text",
  "number",
  "date",
  "boolean",
  "select",
]);

export const customFieldSchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Field name is required"),
  label: z.string().min(1, "Field label is required"),
  type: customFieldTypeSchema,
  required: z.boolean(),
  defaultValue: z.string().optional(),
  options: z.array(z.string()).optional(),
  position: z.number().min(0),
});

export const invoiceTemplateSchema = z.object({
  name: z
    .string()
    .min(1, "Template name is required")
    .max(255, "Name too long"),
  description: z.string().max(1000, "Description too long").optional(),
  isDefault: z.boolean(),
  isActive: z.boolean(),

  // Company Information
  companyName: z.string().max(255, "Company name too long").optional(),
  companyLogoUrl: z
    .string()
    .url("Invalid logo URL")
    .optional()
    .or(z.literal("")),
  companyAddress: z.string().max(1000, "Address too long").optional(),
  companyPhone: z.string().max(50, "Phone too long").optional(),
  companyEmail: emailSchema.optional().or(z.literal("")),
  companyWebsite: z
    .string()
    .url("Invalid website URL")
    .optional()
    .or(z.literal("")),
  companyTaxId: z.string().max(100, "Tax ID too long").optional(),

  // Design Settings
  primaryColor: hexColorSchema,
  secondaryColor: hexColorSchema,
  fontFamily: z.string().min(1, "Font family is required"),
  fontSize: z.number().min(8).max(24, "Font size must be between 8 and 24"),

  // Layout Settings
  showLogo: z.boolean(),
  showCompanyInfo: z.boolean(),
  showTaxBreakdown: z.boolean(),
  showPaymentTerms: z.boolean(),
  showNotes: z.boolean(),

  // Custom Fields
  customFields: z.array(customFieldSchema),

  // Footer Settings
  footerText: z.string().max(500, "Footer text too long").optional(),
  showPageNumbers: z.boolean(),
});

// =====================================================
// ENHANCED INVOICE VALIDATION
// =====================================================

export const invoiceStatusSchema = z.enum([
  "draft",
  "sent",
  "paid",
  "overdue",
  "cancelled",
]);
export const paymentStatusSchema = z.enum([
  "unpaid",
  "partial",
  "paid",
  "overpaid",
]);

export const invoiceItemSchema = z.object({
  productId: z.string().optional(),
  productName: z.string().min(1, "Product name is required"),
  description: z.string().optional(),
  quantity: positiveNumberSchema,
  unitPrice: nonNegativeNumberSchema,
  discountPercentage: percentageSchema,
});

export const enhancedInvoiceFormSchema = z.object({
  clientId: z.string().optional(),
  clientName: z.string().min(1, "Client name is required"),
  clientPhone: z.string().optional(),
  clientEmail: emailSchema.optional().or(z.literal("")),
  clientAddress: z.string().optional(),
  taxRate: percentageSchema,
  discountAmount: nonNegativeNumberSchema,
  dueDate: z.string().optional(),
  notes: z.string().max(2000, "Notes too long").optional(),
  termsAndConditions: z.string().max(2000, "Terms too long").optional(),
  templateId: z.string().optional(),
  items: z.array(invoiceItemSchema).min(1, "At least one item is required"),
});

// =====================================================
// EMAIL VALIDATION
// =====================================================

export const emailTypeSchema = z.enum([
  "invoice_sent",
  "payment_reminder",
  "overdue_notice",
  "payment_confirmation",
  "custom",
]);
export const emailStatusSchema = z.enum([
  "pending",
  "sent",
  "delivered",
  "failed",
  "bounced",
]);

export const emailComposerSchema = z.object({
  recipientEmail: emailSchema,
  subject: z
    .string()
    .min(1, "Subject is required")
    .max(500, "Subject too long"),
  body: z
    .string()
    .min(1, "Email body is required")
    .max(10000, "Email body too long"),
  attachPdf: z.boolean(),
  emailType: emailTypeSchema,
  templateVariables: z.record(z.string()).optional(),
});

export const emailLogSchema = z.object({
  invoiceId: uuidSchema.optional(),
  clientId: uuidSchema.optional(),
  emailType: emailTypeSchema,
  recipientEmail: emailSchema,
  subject: z
    .string()
    .min(1, "Subject is required")
    .max(500, "Subject too long"),
  emailBody: z.string().max(10000, "Email body too long").optional(),
  hasPdfAttachment: z.boolean(),
  attachmentUrls: z.array(z.string().url()),
});

// =====================================================
// SEARCH AND FILTER VALIDATION
// =====================================================

export const dateRangeSchema = z
  .object({
    start: z.date(),
    end: z.date(),
  })
  .refine(
    (data) => data.start <= data.end,
    "Start date must be before or equal to end date"
  );

export const amountRangeSchema = z
  .object({
    min: nonNegativeNumberSchema,
    max: nonNegativeNumberSchema,
  })
  .refine(
    (data) => data.min <= data.max,
    "Minimum amount must be less than or equal to maximum amount"
  );

export const invoiceSearchFiltersSchema = z.object({
  searchTerm: z.string().optional(),
  status: z.array(invoiceStatusSchema).optional(),
  paymentStatus: z.array(paymentStatusSchema).optional(),
  clientId: uuidSchema.optional(),
  dateRange: dateRangeSchema.optional(),
  amountRange: amountRangeSchema.optional(),
  dueDate: dateRangeSchema.optional(),
  templateId: uuidSchema.optional(),
  isOverdue: z.boolean().optional(),
});

export const paymentSearchFiltersSchema = z.object({
  searchTerm: z.string().optional(),
  paymentMethod: z.array(paymentMethodSchema).optional(),
  dateRange: dateRangeSchema.optional(),
  amountRange: amountRangeSchema.optional(),
  invoiceId: uuidSchema.optional(),
});

// =====================================================
// BULK OPERATIONS VALIDATION
// =====================================================

export const bulkOperationTypeSchema = z.enum([
  "status_update",
  "send_email",
  "generate_pdf",
  "delete",
  "payment_reminder",
]);

export const bulkOperationSchema = z.object({
  type: bulkOperationTypeSchema,
  invoiceIds: z
    .array(uuidSchema)
    .min(1, "At least one invoice must be selected"),
  parameters: z.record(z.any()).optional(),
});

// =====================================================
// EXPORT OPTIONS VALIDATION
// =====================================================

export const exportFormatSchema = z.enum(["csv", "xlsx", "pdf"]);
export const groupBySchema = z.enum(["client", "status", "month", "template"]);

export const exportOptionsSchema = z.object({
  format: exportFormatSchema,
  includeFields: z
    .array(z.string())
    .min(1, "At least one field must be selected"),
  filters: invoiceSearchFiltersSchema.optional(),
  groupBy: groupBySchema.optional(),
});

// =====================================================
// PDF GENERATION VALIDATION
// =====================================================

export const pdfFormatSchema = z.enum(["A4", "Letter"]);
export const pdfOrientationSchema = z.enum(["portrait", "landscape"]);

export const pdfGenerationOptionsSchema = z.object({
  templateId: uuidSchema.optional(),
  includePaymentHistory: z.boolean(),
  watermark: z.string().max(100, "Watermark too long").optional(),
  format: pdfFormatSchema,
  orientation: pdfOrientationSchema,
});

// =====================================================
// TYPE INFERENCE HELPERS
// =====================================================

// Infer TypeScript types from Zod schemas
export type PaymentRecordFormData = z.infer<typeof paymentRecordFormSchema>;
export type InvoiceTemplateFormData = z.infer<typeof invoiceTemplateSchema>;
export type EnhancedInvoiceFormData = z.infer<typeof enhancedInvoiceFormSchema>;
export type EmailComposerData = z.infer<typeof emailComposerSchema>;
export type InvoiceSearchFilters = z.infer<typeof invoiceSearchFiltersSchema>;
export type PaymentSearchFilters = z.infer<typeof paymentSearchFiltersSchema>;
export type BulkOperationData = z.infer<typeof bulkOperationSchema>;
export type ExportOptionsData = z.infer<typeof exportOptionsSchema>;
export type PDFGenerationOptionsData = z.infer<
  typeof pdfGenerationOptionsSchema
>;

// =====================================================
// VALIDATION UTILITY FUNCTIONS
// =====================================================

export const validatePaymentRecord = (data: unknown) => {
  return paymentRecordFormSchema.safeParse(data);
};

export const validateInvoiceTemplate = (data: unknown) => {
  return invoiceTemplateSchema.safeParse(data);
};

export const validateEnhancedInvoice = (data: unknown) => {
  return enhancedInvoiceFormSchema.safeParse(data);
};

export const validateEmailComposer = (data: unknown) => {
  return emailComposerSchema.safeParse(data);
};

export const validateInvoiceSearchFilters = (data: unknown) => {
  return invoiceSearchFiltersSchema.safeParse(data);
};

export const validateBulkOperation = (data: unknown) => {
  return bulkOperationSchema.safeParse(data);
};

export const validateExportOptions = (data: unknown) => {
  return exportOptionsSchema.safeParse(data);
};

export const validatePDFGenerationOptions = (data: unknown) => {
  return pdfGenerationOptionsSchema.safeParse(data);
};

// =====================================================
// CUSTOM VALIDATION HELPERS
// =====================================================

export const isValidInvoiceAmount = (
  amount: number,
  maxAmount?: number
): boolean => {
  if (amount <= 0) return false;
  if (maxAmount && amount > maxAmount) return false;
  return true;
};

export const isValidPaymentAmount = (
  paymentAmount: number,
  invoiceTotal: number,
  alreadyPaid: number
): boolean => {
  if (paymentAmount <= 0) return false;
  const remaining = invoiceTotal - alreadyPaid;
  // Allow overpayment up to 10% of invoice total
  const maxAllowed = remaining + invoiceTotal * 0.1;
  return paymentAmount <= maxAllowed;
};

export const isValidDueDate = (dueDate: Date, invoiceDate: Date): boolean => {
  return dueDate >= invoiceDate;
};

export const isValidEmailTemplate = (
  template: string,
  variables: string[]
): boolean => {
  // Check if all required variables are present in the template
  const templateVariables = template.match(/\{\{(\w+)\}\}/g) || [];
  const templateVarNames = templateVariables.map((v) => v.replace(/[{}]/g, ""));

  return templateVarNames.every((varName) => variables.includes(varName));
};

// =====================================================
// ERROR MESSAGE CONSTANTS
// =====================================================

export const VALIDATION_MESSAGES = {
  REQUIRED: "This field is required",
  INVALID_EMAIL: "Please enter a valid email address",
  INVALID_PHONE: "Please enter a valid phone number",
  INVALID_AMOUNT: "Please enter a valid amount",
  INVALID_DATE: "Please enter a valid date",
  INVALID_UUID: "Invalid ID format",
  AMOUNT_TOO_LARGE: "Amount exceeds maximum allowed",
  AMOUNT_TOO_SMALL: "Amount must be greater than zero",
  DATE_IN_PAST: "Date cannot be in the past",
  DUPLICATE_NAME: "This name is already in use",
  INVALID_PERCENTAGE: "Percentage must be between 0 and 100",
  INVALID_COLOR: "Please enter a valid hex color code",
  TEXT_TOO_LONG: "Text exceeds maximum length",
  INVALID_URL: "Please enter a valid URL",
} as const;
